/**
 * The four things the old "roles" list ran together.
 *
 * campusone-identity-service keeps them in four separate tables for a reason, and the
 * distinction is not cosmetic:
 *
 *   Position       — an institutional office. Has a canonical code that authorization
 *                    is written against, and a display title that is only ever a label.
 *   Academic rank  — Professor, Lecturer. GRANTS NO PERMISSIONS. A migration trigger in
 *                    identity refuses to create a role_definition whose code is a rank
 *                    code, so this is enforced in the database, not just by convention.
 *   Role bundle    — a permission set. This, and only this, decides what a person may do.
 *   Assignment     — a person holding a position, over a scope, for a period.
 *
 * Collapsing them is what produced a single list containing "Associate Professor"
 * (a rank, no permissions), "Dean" (an office), "Librarian" (an office) and "Exam
 * Officer" (an office) as if picking any of them meant the same kind of decision.
 */

/* ─── Positions ─── */

/**
 * The categories identity stamps on each position template.
 *
 * Transcribed from PositionCategory in campusone-identity-service
 * (src/positions/position.enums.ts). This list is a mirror, not a contract: a category
 * minted after this bundle shipped still arrives, and POSITION_GROUP_BY_CATEGORY sends
 * anything unrecognised to OTHER rather than dropping the position off the screen.
 */
export const POSITION_CATEGORIES = [
  'INSTITUTION_LEADERSHIP',
  'ACADEMIC_LEADERSHIP',
  'TEACHING',
  'TRAINING_DELIVERY',
  'ASSESSMENT',
  'STUDENT_AFFAIRS',
  'DISCIPLINE',
  'GUIDANCE',
  'ADMINISTRATION',
  'FINANCE',
  'OPERATIONS',
  'QUALITY_ASSURANCE',
  'GOVERNANCE',
  'SAFETY',
] as const;

export type PositionCategory = (typeof POSITION_CATEGORIES)[number];

/** Where a position can be held. Identity's PositionScopeType. */
export const POSITION_SCOPE_TYPES = [
  'INSTITUTION',
  'CAMPUS',
  'SCHOOL_SECTION',
  'FACULTY',
  'DEPARTMENT',
  'PROGRAMME',
  'CLASS',
  'SUBJECT',
  'TRADE',
  'WORKSHOP',
  'GROUP',
  'AUTHORITY_AREA',
] as const;

export type PositionScopeType = (typeof POSITION_SCOPE_TYPES)[number];

/** Identity's EducationSubsystem — which label set a title is read from. */
export const EDUCATION_SUBSYSTEMS = ['ANGLOPHONE', 'FRANCOPHONE', 'BILINGUAL', 'GENERAL'] as const;

export type EducationSubsystem = (typeof EDUCATION_SUBSYSTEMS)[number];

/**
 * How the administrator thinks about the offices, which is not how identity files them.
 *
 * Identity has fourteen categories because it is modelling an office taxonomy across
 * nine institution types. An administrator opening this page is looking for five
 * headings. The grouping is therefore presentation and lives here, over the server's
 * categories — it never replaces them, and every position still carries the category
 * identity gave it.
 */
export const POSITION_GROUPS = [
  'ACADEMIC_LEADERSHIP',
  'ACADEMIC_DELIVERY',
  'ADMINISTRATION',
  'OPERATIONS',
  'FINANCE',
  'OTHER',
] as const;

export type PositionGroup = (typeof POSITION_GROUPS)[number];

export const POSITION_GROUP_BY_CATEGORY: Record<PositionCategory, PositionGroup> = {
  INSTITUTION_LEADERSHIP: 'ACADEMIC_LEADERSHIP',
  ACADEMIC_LEADERSHIP: 'ACADEMIC_LEADERSHIP',
  GOVERNANCE: 'ACADEMIC_LEADERSHIP',
  TEACHING: 'ACADEMIC_DELIVERY',
  TRAINING_DELIVERY: 'ACADEMIC_DELIVERY',
  ASSESSMENT: 'ACADEMIC_DELIVERY',
  ADMINISTRATION: 'ADMINISTRATION',
  STUDENT_AFFAIRS: 'ADMINISTRATION',
  OPERATIONS: 'OPERATIONS',
  DISCIPLINE: 'OPERATIONS',
  GUIDANCE: 'OPERATIONS',
  SAFETY: 'OPERATIONS',
  QUALITY_ASSURANCE: 'OPERATIONS',
  FINANCE: 'FINANCE',
};

/** A category this build does not know is still shown, under OTHER. */
export function groupForCategory(category: string): PositionGroup {
  return POSITION_GROUP_BY_CATEGORY[category as PositionCategory] ?? 'OTHER';
}

export interface PositionTemplate {
  id: string;
  /**
   * The stable identifier authorization is written against. Renaming the display
   * title must never touch this — that separation is the entire point of the model.
   */
  canonicalCode: string;
  institutionType: string;
  category: string;
  defaultDisplayName: string;
  description?: string | null;
  requiresApproval: boolean;
  assignableScopeTypes: PositionScopeType[];
  systemManaged: boolean;
  active: boolean;
}

/**
 * A template with the locale/subsystem title already resolved by the server.
 *
 * `displayTitle` is what the administrator reads; `canonicalCode` is what the
 * authorization layer reads. They are carried side by side deliberately so no screen
 * can present the title as if it were the identifier.
 */
export interface TitledPosition extends PositionTemplate {
  displayTitle: string;
  localCode?: string | null;
}

/**
 * Where a position's display title came from, most specific first.
 *
 * The admin renaming a title needs to see what they are overriding: their own earlier
 * override (INSTITUTION), the shared per-type label (SHARED_LABEL), or the template's
 * default (TEMPLATE_DEFAULT). The server resolves this; the client never guesses.
 */
export const TITLE_SOURCES = ['INSTITUTION', 'SHARED_LABEL', 'TEMPLATE_DEFAULT'] as const;

export type TitleSource = (typeof TITLE_SOURCES)[number];

/**
 * One position as ONE institution sees it — GET /institutions/:id/positions.
 *
 * Three flags interact and the page must render them faithfully:
 *   catalogueActive — the shared catalogue's opinion (the template's `active`).
 *   enabled         — this institution's opinion. `null` is a real third state:
 *                     "no opinion recorded", which falls back to the catalogue.
 *                     It must render as indeterminate, never as ON.
 *   active          — the server's computed verdict: catalogueActive && enabled !== false.
 *
 * When catalogueActive is false the institution cannot revive the position by enabling
 * it — the catalogue retired it — so the toggle is disabled with that reason.
 */
export interface InstitutionPositionView {
  id: string;
  positionTemplateId: string;
  canonicalCode: string;
  category: string;
  institutionType: string;
  defaultDisplayName: string;
  assignableScopeTypes: PositionScopeType[];
  requiresApproval: boolean;
  systemManaged: boolean;
  active: boolean;
  catalogueActive: boolean;
  enabled: boolean | null;
  displayTitle: string;
  titleSource: TitleSource;
  localCode?: string | null;
  description?: string | null;
}

/**
 * A faculty, department, programme… — the scope picker's options.
 *
 * Served from EduCore through identity (`source: 'EDUCORE'`). A 503 from this endpoint
 * means "could not ask EduCore" and must be rendered as that error, never as an empty
 * list — an empty picker reads as "you have no departments", which is a lie.
 */
export interface OrganisationalUnit {
  id: string;
  name: string;
  type: string;
  code?: string | null;
  parentId?: string | null;
  active?: boolean;
}

export interface OrganisationalUnitList {
  items: OrganisationalUnit[];
  source: string;
}

/** Identity's raw paginated body: `{data, total, page, limit, totalPages}`. */
export interface Paginated<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * One row of the institution's appointment register —
 * GET /institutions/:id/position-assignments.
 *
 * Unlike the per-person lookup, the server joins the position (and the rank and bundle
 * when the appointment carries them) into the row, so the client does no stitching.
 */
export interface AppointmentRegisterRow {
  id: string;
  personId: string;
  personName?: string | null;
  position: {
    canonicalCode: string;
    displayTitle: string;
    category: string;
  };
  rank: { id?: string; code?: string; displayName: string; displayNameFr?: string | null } | null;
  bundle: { id?: string; code?: string; displayName: string } | null;
  scopeType: PositionScopeType;
  scopeReference?: string | null;
  scopeDisplayName?: string | null;
  campusId?: string | null;
  acting: boolean;
  status: string;
  approvalStatus?: string | null;
  validFrom: string;
  validUntil?: string | null;
}

/**
 * The one-transaction appointment — POST /institutions/:id/appointments.
 *
 * Position, optional rank, optional permission bundle, recorded together so a person
 * is never left holding an office without the access (or standing) that was meant to
 * come with it. Field names mirror CreateAppointmentDto exactly: identity validates
 * with forbidNonWhitelisted, so an extra field is a 400, not a shrug.
 */
export interface CreateAppointmentRequest {
  personId: string;
  positionCode: string;
  scopeType: PositionScopeType;
  /** Required whenever scopeType !== INSTITUTION — an organisational-unit id. */
  scopeReference?: string;
  scopeDisplayName?: string;
  campusId?: string;
  acting?: boolean;
  validFrom: string;
  validUntil?: string;
  academicRankId?: string;
  rankEvidenceReference?: string;
  roleDefinitionId?: string;
  reason?: string;
}

export interface CreateAppointmentResponse {
  positionAssignment: PositionAssignment;
  rankAssignment: AcademicRankAssignment | null;
  roleAssignment: SecurityRoleAssignment | null;
}

export interface PositionLabel {
  id: string;
  positionTemplateId?: string;
  locale: string;
  educationSubsystem: EducationSubsystem;
  displayName: string;
  localCode?: string | null;
}

/* ─── Academic ranks ─── */

/**
 * An academic rank. Carries no permissions and cannot be made to.
 *
 * `rankOrder` is 1 for the most senior, which is why the page sorts ascending.
 */
export interface AcademicRank {
  id: string;
  code: string;
  displayName: string;
  displayNameFr?: string | null;
  description?: string | null;
  rankOrder: number;
  aliasCodes: string[];
  active: boolean;
}

export interface AcademicRankAssignment {
  id: string;
  personId: string;
  academicRankId: string;
  institutionId?: string | null;
  effectiveFrom: string;
  effectiveUntil?: string | null;
  conferredBy?: string | null;
  evidenceReference?: string | null;
  /** Present when the server expands the rank; the page falls back to a lookup by id. */
  academicRank?: AcademicRank;
}

/* ─── Security roles / permission bundles ─── */

/**
 * A permission bundle — identity's role_definition.
 *
 * `institutionId` null means a platform catalogue role offered to every institution;
 * non-null means one this institution composed for itself.
 */
export interface RoleBundle {
  id: string;
  code: string;
  displayName: string;
  description?: string | null;
  roleCategory?: string | null;
  scopeRequirement?: string | null;
  requiresApproval: boolean;
  systemManaged: boolean;
  institutionId?: string | null;
  active: boolean;
}

export interface SecurityRoleAssignment {
  id: string;
  personId: string;
  roleDefinitionId: string;
  status: string;
  validFrom?: string | null;
  validUntil?: string | null;
  campusId?: string | null;
  departmentReference?: string | null;
  /** Present when the server expands the definition. */
  roleDefinition?: Pick<RoleBundle, 'id' | 'code' | 'displayName'>;
}

/* ─── Assignments ─── */

export interface PositionAssignment {
  id: string;
  personId: string;
  positionTemplateId: string;
  institutionId?: string | null;
  campusId?: string | null;
  scopeType: PositionScopeType;
  scopeReference?: string | null;
  scopeDisplayName?: string | null;
  acting: boolean;
  status: string;
  approvalStatus?: string | null;
  validFrom: string;
  validUntil?: string | null;
  reason?: string | null;
  endedAt?: string | null;
}

/**
 * One appointment as an administrator reads it:
 * "Dr Nfor — Position: Head of Department · Rank: Associate Professor ·
 *  Bundle: Department Administrator · Scope: Department of Computer Science".
 *
 * Assembled in this app from three separate calls, because identity stores the three
 * facts in three tables with no join between them: position_assignments has no rank
 * column and no role_definition column.
 *
 * `bundles` is a LIST and belongs to the person, not to this appointment — there is no
 * link from an appointment to a permission bundle, so presenting one bundle as "the
 * bundle for this position" would be an invention. The page says so where it renders.
 */
export interface AppointmentRow {
  positionAssignment: PositionAssignment;
  position: TitledPosition | null;
  rank: AcademicRank | null;
  bundles: RoleBundle[];
  scopeLabel: string;
}

/* ─── Terminology ─── */

/**
 * The vocabulary keys institution_templates.terminology carries.
 *
 * Read from the server per institution type, never hardcoded per screen: a secondary
 * school says Class / Term / Sequence where a university says Course Offering /
 * Semester / Continuous Assessment, and both are the same page.
 */
export const TERMINOLOGY_KEYS = [
  'learner',
  'learnerPlural',
  'educator',
  'educatorPlural',
  'teachingGroup',
  'headOfInstitution',
  'academicUnit',
  'programmeOfStudy',
  'academicPeriod',
  'academicYear',
  'assessment',
  'guardian',
] as const;

export type TerminologyKey = (typeof TERMINOLOGY_KEYS)[number];

export type Terminology = Partial<Record<TerminologyKey, string>>;

export interface InstitutionTemplate {
  code: string;
  institutionType: string;
  educationSubsystem: EducationSubsystem;
  displayName: string;
  description?: string | null;
  capabilityClass?: string | null;
  terminology: Terminology;
  defaultRoleBundleCodes: string[];
  defaultPositions: { positionCode: string; label?: string | null }[];
}
